/*
 * Register the Service Worker that powers offline clock-in queuing.
 *
 * Side-effects:
 *  - Registers /sw.js on page load
 *  - On 'online' events (iOS Safari fallback), messages the SW to flush the queue
 *  - Listens for SW → page messages and toasts the user when a queued clock-in finally succeeds
 */
import { useEffect } from "react";
import { toast } from "sonner";

export default function ServiceWorkerBootstrap() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator)) return;

    // Register on load (so the heavy page JS has time to start)
    const onLoad = () => {
      navigator.serviceWorker
        .register("/sw.js", { scope: "/" })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("SW register failed:", err);
        });
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad);

    // Listen for messages from the SW so we can surface queued/sync events
    const onSwMsg = (evt) => {
      const data = evt.data || {};
      if (data.type === "clock-in-queued") {
        toast("You're offline — clock-in queued", {
          description: "We'll sync it automatically when you're back online.",
          duration: 6000,
        });
      } else if (data.type === "clock-in-synced") {
        toast.success("Queued clock-in synced", {
          description: "Your offline clock-in has been posted to the server.",
          duration: 5000,
        });
      }
    };
    navigator.serviceWorker.addEventListener("message", onSwMsg);

    // iOS Safari doesn't support Background Sync — nudge the SW manually
    // whenever the tab regains connectivity.
    const onOnline = () => {
      navigator.serviceWorker.controller?.postMessage({ type: "sw-flush" });
    };
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("load", onLoad);
      window.removeEventListener("online", onOnline);
      navigator.serviceWorker.removeEventListener("message", onSwMsg);
    };
  }, []);

  return null;
}
